/**
 * Remove o operador de comparação de uma regra da política.
 */
export function extractMinimumVersion({ policyRule }: { policyRule: string }): string {
  return policyRule.replace('>=', '')
}

/**
 * Padroniza os separadores de uma versão.
 *
 * O Bowser pode devolver `_` como separador em vez de `.` (ex.: "16_4"),
 * porque o token do user agent aceita as duas formas.
 */
function normalizeVersionSeparators({ version }: { version: string }): string {
  return version.replace(/[_\s]/g, '.')
}

/**
 * Um segmento só é comparável quando contém apenas dígitos: `Number('')`
 * seria `0` e `Number('16b')` seria `NaN`.
 */
function isNumericSegment(segment: string): boolean {
  return /^\d+$/.test(segment)
}

/**
 * Converte uma versão em segmentos numéricos, ou `undefined` quando algum
 * segmento não é um número — caso em que a versão não pode ser comparada.
 */
function parseVersionSegments({ version }: { version: string }): number[] | undefined {
  const segments = normalizeVersionSeparators({ version }).split('.')

  if (!segments.every(isNumericSegment)) {
    return undefined
  }

  return segments.map(Number)
}

/**
 * Normaliza uma versão para exibição, removendo zeros desnecessários
 * e garantindo pelo menos dois segmentos.
 *
 * Ex.: "150.0.0.0" → "150.0"
 */
export function formatVersionForDisplay({ version }: { version: string }): string {
  const segments = normalizeVersionSeparators({ version }).split('.')

  while (segments.length > 2 && segments[segments.length - 1] === '0') {
    segments.pop()
  }

  while (segments.length < 2) {
    segments.push('0')
  }

  return segments.join('.')
}

/**
 * Formata a versão mínima de uma regra da política para exibição.
 */
export function formatMinimumVersionForDisplay({ policyRule }: { policyRule: string }): string {
  return formatVersionForDisplay({
    version: extractMinimumVersion({ policyRule }),
  })
}

/**
 * Verifica se uma versão atende à versão mínima
 */
export function meetsMinimumVersion({
  policyRule,
  version,
}: {
  version: string
  policyRule: string
}): boolean {
  const currentSegments = parseVersionSegments({ version })
  const minimumSegments = parseVersionSegments({ version: extractMinimumVersion({ policyRule }) })

  /**
   * Versão sem formato numérico reconhecível não bloqueia o acesso:
   * `supportsRegexLookbehind` continua sendo a barreira real e um falso
   * positivo aqui trancaria o usuário fora do sistema — no Safari e nos
   * bloqueios de sistema não existe a opção de avançar.
   */
  if (!currentSegments || !minimumSegments) {
    return true
  }

  for (let index = 0; index < minimumSegments.length; index += 1) {
    const currentSegment = currentSegments[index] ?? 0

    if (currentSegment !== minimumSegments[index]) {
      return currentSegment > minimumSegments[index]
    }
  }

  return true
}
